import { TTokenRedemptionOptionSelectResponse } from '@daymade/api-contracts';
import {
  TokenRedemptionOptionGroup,
  TokenRedemptionOptionType,
} from '@daymade/common-lib/dbEnums';
import MainLayout from 'components/layout/MainLayout';
import ALittleLostSection from 'components/token-town/a-little-lost-section';
import ImageGallery from 'components/token-town/draws/image-gallery/ImageGallery';
import WeeklyDrawPurchase from 'components/token-town/draws/weekly-draw/WeeklyDrawPurchase';
import Header from 'components/token-town/header/Header';
import BackLink from 'components/token-town/links/BackLink';
import PageGrid from 'components/token-town/page-grid/PageGrid';
import Page from 'components/token-town/page/Page';
import PurchaseTokensBanner from 'components/token-town/purchase-tokens-banner/PurchaseTokensBanner';
import { useUserStore } from 'hooks/stores/useUserStore';
import { observer } from 'mobx-react-lite';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { tokenApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';

interface IWeeklyDrawEntryPage {
  weeklyDrawEntryDetails: TTokenRedemptionOptionSelectResponse | null;
}

const WeeklyDrawEntryPage = observer(
  ({ weeklyDrawEntryDetails }: IWeeklyDrawEntryPage) => {
    const router = useRouter();

    const { currentUser, balances } = useUserStore();

    const tokenBalance = balances?.tokenBalance ?? 0;

    useEffect(() => {
      if (router.isReady && !weeklyDrawEntryDetails) {
        router.replace('/404');
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router.isReady, weeklyDrawEntryDetails]);

    if (!weeklyDrawEntryDetails) {
      return null;
    }

    return (
      <MainLayout>
        <NextSeo
          title={`${weeklyDrawEntryDetails.title} | Token Town | Win Epic Prizes | DAYMADE`}
          description={`${weeklyDrawEntryDetails.description}`}
          openGraph={{
            title: `${weeklyDrawEntryDetails.title} | Token Town | Win Epic Prizes | DAYMADE`,
            description: `${weeklyDrawEntryDetails.description}`,
          }}
        />
        <Header />

        <Page>
          {currentUser && <PurchaseTokensBanner tokenCount={tokenBalance} />}

          <PageGrid splitEarly>
            <ImageGallery
              tokenCost={weeklyDrawEntryDetails.price ?? 0}
              images={[weeklyDrawEntryDetails.heroImage]}
            />

            {weeklyDrawEntryDetails.title &&
              weeklyDrawEntryDetails.drawDetails &&
              weeklyDrawEntryDetails.drawDetails.numberSetup && (
                <WeeklyDrawPurchase
                  title={weeklyDrawEntryDetails.title}
                  playerTokenCount={tokenBalance}
                  entryCost={weeklyDrawEntryDetails.price}
                  numberRanges={weeklyDrawEntryDetails.drawDetails.numberSetup}
                  redemptionOptionId={weeklyDrawEntryDetails.id}
                />
              )}
          </PageGrid>

          <BackLink to="prizes" />
        </Page>
        <ALittleLostSection />
      </MainLayout>
    );
  },
);

export const getServerSideProps: GetServerSideProps<
  IWeeklyDrawEntryPage
> = async (ctx) => {
  const { id } = ctx.query;
  let tokenRedemptionOption = null;

  const parsedId = Number(id);
  if (isNaN(parsedId)) {
    return {
      props: {
        weeklyDrawEntryDetails: tokenRedemptionOption,
      },
    };
  }

  const resp = await tokenApiClient.public.getTokenRedemptionOption({
    params: {
      id: parsedId,
    },
  });

  switch (resp.status) {
    case HttpStatusCode.OK:
      if (
        !(
          resp.body.type === TokenRedemptionOptionType.ENTRY &&
          resp.body.group === TokenRedemptionOptionGroup.PRIZE
        )
      ) {
        return {
          props: {
            weeklyDrawEntryDetails: tokenRedemptionOption,
          },
        };
      }
      tokenRedemptionOption = {
        ...resp.body,
      };

      break;
    case HttpStatusCode.NOT_FOUND:
      console.error(
        `Error fetching tree redemption details: ${resp.body.message}`,
      );
      return {
        props: {
          weeklyDrawEntryDetails: tokenRedemptionOption,
        },
      };
  }

  ctx.res.setHeader(
    'Cache-Control',
    'public, max-age=3600, s-maxage=3600, stale-while-revalidate=59',
  );

  return {
    props: {
      weeklyDrawEntryDetails: JSON.parse(JSON.stringify(tokenRedemptionOption)),
    },
  };
};

export default WeeklyDrawEntryPage;
